// ============================================================================
// COMPLIANCE USER - REFERENCE MATERIALS PAGE
// ============================================================================
// Purpose: View reference materials and guidance for compliance work
// Mental model: "Show me helpful resources for my compliance work."
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@contexts/AuthContext';
import {
  Book,
  FileText,
  Search,
  AlertTriangle,
  RefreshCw,
  Download,
  Eye,
  X
} from 'lucide-react';
import { referenceMaterialsApi } from '@domains/compliance/execution/api/referenceMaterials.api';
import { toast } from 'react-toastify';
import ReactFlow, {
  Node,
  Edge,
  Background,
  Controls,
  MiniMap,
  BackgroundVariant,
  NodeTypes,
  MarkerType,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import PageInfoButton from '@/components/PageInfoButton';

// Combined interface for reference materials from guidance and process maps
interface ReferenceMaterial {
  id: number;
  title: string;
  description: string;
  type: 'guidance' | 'process_map';
  category: string;
  content?: string;
  diagramData?: string;
  frameworkName?: string;
  lastUpdated: string;
}

// React Flow node types
const nodeTypes: NodeTypes = {
  start: ({ data }: { data: any }) => (
    <div className="px-4 py-2 rounded-lg bg-green-100 border-2 border-green-500 text-sm font-medium text-green-800 relative">
      <Handle type="source" position={Position.Bottom} />
      {data.label}
    </div>
  ),
  process: ({ data }: { data: any }) => (
    <div className="px-4 py-2 rounded-lg bg-slate-100 border-2 border-slate-400 text-sm font-medium text-slate-800 relative">
      <Handle type="target" position={Position.Top} />
      <Handle type="source" position={Position.Bottom} />
      {data.label}
    </div>
  ),
  decision: ({ data }: { data: any }) => (
    <div className="px-4 py-2 rounded-lg bg-yellow-100 border-2 border-yellow-500 text-sm font-medium text-yellow-800 relative">
      <Handle type="target" position={Position.Top} />
      <Handle type="source" position={Position.Bottom} />
      {data.label}
    </div>
  ),
  end: ({ data }: { data: any }) => (
    <div className="px-4 py-2 rounded-lg bg-red-100 border-2 border-red-500 text-sm font-medium text-red-800 relative">
      <Handle type="target" position={Position.Top} />
      {data.label}
    </div>
  ),
};

// Process Map Viewer Component using React Flow
const ProcessMapViewer: React.FC<{ diagramData: string }> = ({ diagramData }) => {
  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try {
      const data = JSON.parse(diagramData);
      console.log('ProcessMapViewer data:', data);

      if (data.nodes && Array.isArray(data.nodes) && data.edges && Array.isArray(data.edges)) {
        // Transform nodes to React Flow format
        const flowNodes: Node[] = data.nodes.map((node: any, index: number) => {
          const x = node.x !== undefined ? node.x : (index % 3) * 200 + 50;
          const y = node.y !== undefined ? node.y : Math.floor(index / 3) * 100 + 50;

          return {
            id: String(node.id || index),
            type: node.type || 'process',
            position: { x, y },
            data: { label: node.label || node.name || `Node ${index + 1}` },
            style: {
              width: 150,
              height: 60,
            },
          };
        });

        // Transform edges to React Flow format
        const flowEdges: Edge[] = data.edges.map((edge: any, index: number) => ({
          id: `edge-${index}`,
          source: String(edge.from || edge.source || index),
          target: String(edge.to || edge.target || index + 1),
          label: edge.label || edge.name || '',
          type: 'smoothstep',
          animated: true,
          markerEnd: { type: MarkerType.ArrowClosed },
          style: { strokeWidth: 2, stroke: '#374151' },
        }));

        // If no edges provided, create a linear flow
        let finalEdges = flowEdges;
        if (flowEdges.length === 0 && flowNodes.length > 1) {
          finalEdges = flowNodes.slice(0, -1).map((node, index) => {
            const nextNode = flowNodes[index + 1];
            if (!nextNode) return null;
            return {
              id: `default-edge-${index}`,
              source: node.id,
              target: nextNode.id,
              type: 'smoothstep',
              animated: true,
              markerEnd: { type: MarkerType.ArrowClosed },
              style: { strokeWidth: 2, stroke: '#374151' },
            };
          }).filter((edge): edge is NonNullable<typeof edge> => edge !== null);
        }

        setNodes(flowNodes);
        setEdges(finalEdges);
        setError(null);
      } else {
        setError('Invalid diagram data format');
      }
    } catch (err) {
      setError('Failed to parse diagram data');
    }
  }, [diagramData]);

  if (error) {
    return (
      <div className="text-center py-8">
        <AlertTriangle className="mx-auto text-slate-400 mb-3" size={36} />
        <p className="text-slate-500 text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 rounded-lg p-4">
      <h3 className="font-semibold text-sm text-slate-900 mb-3">Process Flow Diagram</h3>
      <div className="bg-white border border-slate-200 rounded-lg" style={{ height: '400px' }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          fitView
          attributionPosition="bottom-left"
        >
          <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
          <Controls />
          <MiniMap />
        </ReactFlow>
      </div>
      <p className="text-xs text-slate-500 mt-3">
        This diagram shows the process flow for this compliance requirement. Use the controls to zoom and pan.
      </p>
    </div>
  );
};

const ReferenceMaterials: React.FC = () => {
  const { hasPermission } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [referenceMaterials, setReferenceMaterials] = useState<ReferenceMaterial[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewModal, setViewModal] = useState<{
    isOpen: boolean;
    material: ReferenceMaterial | null;
  }>({ isOpen: false, material: null });

  // Load reference materials from the compliance API
  const loadReferenceMaterials = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const materials = await referenceMaterialsApi.getReferenceMaterials();
      setReferenceMaterials(materials);
      setSelectedCategory('all');
    } catch (err) {
      console.error('Error loading reference materials:', err);
      setError('Failed to load reference materials');
      toast.error('Failed to load reference materials');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReferenceMaterials();
  }, [loadReferenceMaterials]);

  // Compute categories from all materials, filtering out empty/null categories
  const categories = useMemo(() => {
    const uniqueCategories = Array.from(new Set(
      referenceMaterials
        .map(m => m.category)
        .filter(category => category && category.trim() !== '')
    ));
    return ['all', ...uniqueCategories];
  }, [referenceMaterials]);

  // Filter materials based on search and category
  const filteredMaterials = useMemo(() => {
    return referenceMaterials.filter(material => {
      const matchesSearch = material.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           material.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || material.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [referenceMaterials, searchTerm, selectedCategory]);

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'guidance':
        return <Book className="text-blue-600" size={16} />;
      case 'process_map':
        return <FileText className="text-green-600" size={16} />;
      default:
        return <FileText className="text-slate-400" size={16} />;
    }
  };

  const getTypeBadge = (type: string) => {
    switch (type) {
      case 'guidance':
        return <span className="px-2 py-1 rounded-md font-bold text-[10px] text-blue-700 bg-blue-100">Guidance</span>;
      case 'process_map':
        return <span className="px-2 py-1 rounded-md font-bold text-[10px] text-green-700 bg-green-100">Process Map</span>;
      default:
        return <span className="px-2 py-1 rounded-md font-bold text-[10px] text-slate-600 bg-slate-100">{type}</span>;
    }
  };

  const handleViewMaterial = useCallback((material: ReferenceMaterial) => {
    setViewModal({ isOpen: true, material });
  }, []);

  const handleDownloadMaterial = useCallback((material: ReferenceMaterial) => {
    try {
      let content = '';
      let filename = '';
      let mimeType = '';

      if (material.type === 'guidance' && material.content) {
        content = material.content;
        filename = `${material.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.html`;
        mimeType = 'text/html';
      } else if (material.type === 'process_map' && material.diagramData) {
        content = material.diagramData;
        filename = `${material.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.json`;
        mimeType = 'application/json';
      } else {
        toast.error('No content available for download');
        return;
      }

      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('Download started');
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download material');
    }
  }, []);

  const closeViewModal = useCallback(() => {
    setViewModal({ isOpen: false, material: null });
  }, []);

  if (!hasPermission('compliance.read')) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl border border-slate-100 p-8">
          <AlertTriangle className="text-red-500 mb-3" size={36} />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Access Denied</h1>
          <p className="text-slate-500 text-sm">You don't have permission to view reference materials.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">Reference Materials</h1><PageInfoButton /></div>
            <p className="text-slate-500 text-sm">Helpful resources for your compliance work</p>
          </div>
          <button
            onClick={loadReferenceMaterials}
            className="px-4 py-2.5 bg-white border border-slate-200 text-slate-700 font-medium text-sm rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-2"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>

        {/* Search and Filter */}
        <div className="bg-white rounded-3xl border border-slate-100 p-4 mb-4">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input
                type="text"
                placeholder="Search reference materials..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
            </div>
            <div className="md:w-56">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white"
              >
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category === 'all' ? 'All Categories' : category}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-3xl border border-slate-100 p-12">
            <div className="flex items-center justify-center">
              <RefreshCw className="text-blue-600 animate-spin" size={40} />
              <span className="ml-3 text-slate-500 text-sm">Loading reference materials...</span>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="bg-white rounded-xl border border-red-200 p-6">
            <div className="text-center">
              <AlertTriangle className="mx-auto text-red-500 mb-3" size={36} />
              <h3 className="text-base font-semibold text-slate-900 mb-1">Error Loading Materials</h3>
              <p className="text-slate-500 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Materials Grid */}
        {!loading && !error && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredMaterials.length === 0 ? (
              <div className="col-span-full bg-white rounded-3xl border border-slate-100 p-12">
                <Book className="text-slate-300 mb-3" size={40} />
                <h3 className="text-base font-semibold text-slate-900 mb-1">No materials found</h3>
                <p className="text-slate-500 text-sm max-w-md mx-auto">
                  {searchTerm || selectedCategory !== 'all'
                    ? 'Try adjusting your search or filter criteria'
                    : 'No reference materials available at this time.'}
                </p>
              </div>
            ) : (
              filteredMaterials.map((material, index) => (
                <div
                  key={`${material.id}-${index}`}
                  className="bg-white rounded-3xl border border-slate-100 hover:border-slate-300 transition-colors overflow-hidden"
                >
                  {/* Material Header */}
                  <div className="px-4 py-3 border-b border-slate-100">
                    <div className="flex items-start gap-2.5">
                      <div className="shrink-0 mt-0.5">
                        {getTypeIcon(material.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-sm text-slate-900 truncate">
                          {material.title}
                        </h3>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          {getTypeBadge(material.type)}
                          <span className="text-xs text-slate-500">{material.category}</span>
                          {material.frameworkName && (
                            <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                              {material.frameworkName}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Material Body */}
                  <div className="px-4 py-3">
                    <p className="text-xs text-slate-500 mb-3 line-clamp-3">
                      {material.description}
                    </p>
                    <div className="text-xs text-slate-400">
                      Updated: {new Date(material.lastUpdated).toLocaleDateString()}
                    </div>
                  </div>

                  {/* Material Footer */}
                  <div className="px-4 py-3 bg-slate-50 border-t border-slate-100">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleViewMaterial(material)}
                        className="flex-1 px-4 py-2.5 bg-blue-600 text-white font-medium text-sm rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                      >
                        <Eye size={14} />
                        View
                      </button>
                      <button
                        onClick={() => handleDownloadMaterial(material)}
                        className="px-4 py-2.5 bg-white border border-slate-200 text-slate-700 font-medium text-sm rounded-lg hover:bg-slate-50 transition-colors flex items-center justify-center gap-2"
                      >
                        <Download size={14} />
                        Download
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* View Modal */}
        {viewModal.isOpen && viewModal.material && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col ml-64">
              {/* Modal Header */}
              <div className="bg-slate-50 border-b border-slate-100 px-5 py-4 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-3">
                  {getTypeIcon(viewModal.material.type)}
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">{viewModal.material.title}</h2>
                    <div className="mt-0.5">{getTypeBadge(viewModal.material.type)}</div>
                  </div>
                </div>
                <button
                  onClick={closeViewModal}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-5 flex-1 overflow-y-auto">
                {viewModal.material.type === 'guidance' && viewModal.material.content ? (
                  <div
                    className="prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: viewModal.material.content }}
                  />
                ) : viewModal.material.type === 'process_map' && viewModal.material.diagramData ? (
                  <ProcessMapViewer diagramData={viewModal.material.diagramData} />
                ) : (
                  <div className="text-center py-8">
                    <AlertTriangle className="mx-auto text-slate-400 mb-3" size={36} />
                    <p className="text-slate-500 text-sm">No content available for this material.</p>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="bg-slate-50 border-t border-slate-200 px-5 py-3 flex justify-end gap-2 shrink-0">
                <button
                  onClick={() => handleDownloadMaterial(viewModal.material!)}
                  className="px-4 py-2.5 bg-white border border-slate-200 text-slate-700 font-medium text-sm rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-2"
                >
                  <Download size={14} />
                  Download
                </button>
                <button
                  onClick={closeViewModal}
                  className="px-4 py-2.5 bg-slate-100 text-slate-600 font-medium text-sm rounded-lg hover:bg-slate-200 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReferenceMaterials;
